"use client"

import { useCallback, useEffect, useState } from "react"
import { createRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"

import { EditTaskDialog } from "@/components/edit-task-dialog"
import { NewTaskDialog } from "@/components/new-task-dialog"
import { PageAction, PageHeader } from "@/components/graphite/page-header"
import { SelectChip } from "@/components/graphite/select-chip"
import { GraphiteTasksBoard } from "@/components/ops/graphite-tasks-board"
import { useOpsTasks, type TaskOwner } from "@/hooks/use-ops-tasks"
import { stageHex } from "@/lib/stage-colors"
import { taskId as idOf } from "@/lib/tasks"
import MainAppRoute from "@/rootRoutes/MainApp"
import type { Task } from "openapi/api/model"

type TasksSearch = {
  createTask?: boolean
  taskId?: string
}

/**
 * Normalizes task modal query state for browser links and assistant deep links.
 * TanStack can parse numeric-looking query values as numbers, so IDs are
 * converted back to strings before the selection hook compares them to tasks.
 */
function validateTasksSearch(search: Record<string, unknown>): TasksSearch {
  const rawCreate = search.createTask
  const createTask =
    rawCreate === true || rawCreate === "true" || rawCreate === "1" || rawCreate === 1

  const rawTaskId = search.taskId
  const taskId =
    typeof rawTaskId === "string" && rawTaskId.trim()
      ? rawTaskId
      : typeof rawTaskId === "number" && Number.isFinite(rawTaskId)
        ? String(rawTaskId)
        : undefined

  return {
    createTask: createTask ? true : undefined,
    taskId,
  }
}

const TasksPageRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/ops/tasks",
  component: TasksPage,
  validateSearch: validateTasksSearch,
})
export default TasksPageRoute

type TasksNavigate = ReturnType<typeof TasksPageRoute.useNavigate>

/**
 * The route owns task selection so assistant links, card clicks, and browser
 * back/forward all open the same edit dialog without duplicating selection state.
 */
function useTaskUrlSelection({
  search,
  tasksById,
  tasksLoaded,
  navigate,
}: {
  search: TasksSearch
  tasksById: Map<string, Task>
  tasksLoaded: boolean
  navigate: TasksNavigate
}) {
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false)

  const clearTaskId = useCallback(() => {
    navigate({
      search: (prev: TasksSearch) => ({ ...prev, taskId: undefined }),
      replace: true,
    })
  }, [navigate])

  useEffect(() => {
    if (!search.taskId) {
      setEditingTask(null)
      setIsEditTaskDialogOpen(false)
      return
    }

    const task = tasksById.get(search.taskId)
    if (task) {
      setEditingTask(task)
      setIsEditTaskDialogOpen(true)
      return
    }

    if (tasksLoaded) clearTaskId()
  }, [clearTaskId, search.taskId, tasksById, tasksLoaded])

  const openTask = useCallback(
    (task: Task) => {
      /*
        Either id the API sent. This read `task_id` alone, so a row that came
        back carrying only `id` — which is the name the model declares first —
        produced a card that could be clicked and did nothing.
      */
      const selected = idOf(task)
      if (!selected) return

      navigate({
        search: (prev: TasksSearch) => ({
          ...prev,
          createTask: undefined,
          taskId: selected,
        }),
        replace: true,
      })
    },
    [navigate],
  )

  const setEditTaskDialogOpen = useCallback(
    (open: boolean) => {
      setIsEditTaskDialogOpen(open)

      if (!open) {
        setEditingTask(null)
        clearTaskId()
      }
    },
    [clearTaskId],
  )

  return { editingTask, isEditTaskDialogOpen, openTask, setEditTaskDialogOpen }
}

/**
 * 33a's filter reads "Assigned to me". A task on this board has no assignee:
 * `assigned_to` exists in the contract only on pipeline tasks, which are a
 * different resource — `GET /tasks` answers with a `Task`, whose only person is
 * `created_by`. So the control is offered over that, and named for what it
 * actually does rather than for what the artboard wished it did.
 */
const OWNER_OPTIONS: { value: TaskOwner; label: string }[] = [
  { value: "everyone", label: "Everyone's tasks" },
  { value: "mine", label: "Created by me" },
]

function TasksPage() {
  const navigate = TasksPageRoute.useNavigate()
  const search = TasksPageRoute.useSearch()
  const tasks = useOpsTasks()

  const { editingTask, isEditTaskDialogOpen, openTask, setEditTaskDialogOpen } =
    useTaskUrlSelection({
      search,
      tasksById: tasks.byId,
      tasksLoaded: !tasks.isLoading,
      navigate,
    })

  const openCreateTask = () =>
    navigate({
      search: (prev: TasksSearch) => ({ ...prev, createTask: true, taskId: undefined }),
      replace: true,
    })

  const closeCreateTask = () =>
    navigate({
      search: (prev: TasksSearch) => ({ ...prev, createTask: undefined }),
      replace: true,
    })

  const { receipts } = tasks

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <PageHeader
        title="Tasks"
        description="Everything the desk owes, and what it is waiting on"
        actions={
          <div className="flex items-center gap-2.5">
            <SelectChip
              options={OWNER_OPTIONS}
              value={tasks.owner}
              onChange={tasks.setOwner}
              listLabel="Whose tasks to show"
              active={tasks.owner !== "everyone"}
              mono={false}
              className="flex-none"
              triggerClassName="py-2"
            />
            <PageAction tone="primary" onClick={openCreateTask}>
              <Plus className="size-[15px]" />
              New Task
            </PageAction>
          </div>
        }
      >
        {/*
          The receipts line, in place of the four stat cards. Absent until the
          board has been read — every figure comes from an empty array until
          then, so it would otherwise state a confident row of zeroes.
        */}
        {!tasks.isLoading && !tasks.isError && (
          <p className="mt-2.5 font-mono text-[11.5px] leading-none text-muted-foreground">
            {receipts.open} open · {receipts.blocked} blocked ·{" "}
            <span
              style={
                receipts.overdue > 0 ? { color: stageHex("rejected") } : undefined
              }
            >
              {receipts.overdue} overdue
            </span>{" "}
            · {receipts.dueThisWeek} due this week · {receipts.closedSinceMonday} closed
            since Monday
          </p>
        )}
      </PageHeader>

      <GraphiteTasksBoard
        board={tasks.board}
        byId={tasks.byId}
        initials={tasks.initials}
        isLoading={tasks.isLoading}
        isError={tasks.isError}
        onOpenTask={openTask}
        onMove={tasks.move}
      />

      <NewTaskDialog
        open={!!search.createTask}
        onOpenChange={closeCreateTask}
        onCreateTask={tasks.create}
      />

      <EditTaskDialog
        open={isEditTaskDialogOpen}
        onOpenChange={setEditTaskDialogOpen}
        task={editingTask}
        onUpdateTask={tasks.update}
        onDeleteTask={tasks.destroy}
      />
    </div>
  )
}
